import { Input } from "../../../src/components/ui/input";
import React from "react";
import { Button } from "../../../src/components/ui/button";
import { toast } from "../../../src/components/ui/use-toast";
import { Coupon } from "../../../src/lib/interfaces";
import { Loader2 } from "lucide-react";
export default function Coupons({
  total,
  returnDiscountCode,
  shippingRate,
}: {
  total: number;
  returnDiscountCode: (e: string) => void;
  shippingRate: number;
}) {
  const [couponCode, setCouponCode] = React.useState("");
  const [discount, setDiscount] = React.useState("");
  const [newTotal, setNewTotal] = React.useState(total);
  const [type, setType] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (couponCode === "") {
      localStorage.removeItem("couponCode");
      localStorage.removeItem("discount");
      localStorage.removeItem("discountType");
      localStorage.removeItem("newTotal");
      setNewTotal(total);
      setDiscount("");
    }
  }, [total, discount, couponCode]);

  React.useEffect(() => {
    const storedCouponCode =
      localStorage.getItem("couponCode");
    const storedDiscount = localStorage.getItem("discount");
    const storedDiscountType =
      localStorage.getItem("discountType");
    const newTotal = localStorage.getItem("newTotal");

    if (
      storedCouponCode &&
      storedDiscount &&
      storedDiscountType &&
      newTotal
    ) {
      setCouponCode(storedCouponCode);
      setDiscount(storedDiscount);
      setType(storedDiscountType);
      setNewTotal(Number(newTotal));
    }
  }, []);
  React.useEffect(() => {
    if (couponCode === "") {
      setNewTotal(total);
      setDiscount("");
      returnDiscountCode("0");
      localStorage.setItem(
        "newTotal",
        String(Number(total) + Number(shippingRate))
      );
    }
  }, [couponCode, total]);
  const fetchCouponDetails = async (
    couponCode: string
  ): Promise<Coupon> => {
    try {
      const response = await fetch(
        `https://amadeussabra.com/amadeus/wp-json/coupons/v1/get-coupon?couponCode=${encodeURIComponent(
          couponCode
        )}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch coupon");
      }

      const data: Coupon = await response.json();

      return data; // Returns coupon details or an empty object
    } catch (error) {
      return {
        id: 0,
        code: "",
        amount: "0",
        date_created: "",
        date_created_gmt: "",
        date_modified: "",
        date_modified_gmt: "",
        discount_type: "",
        description: "",
        date_expires: "",
        date_expires_gmt: "",
        usage_count: 0,
        individual_use: false,
        product_ids: [],
        excluded_product_ids: [],
        usage_limit: 0,
        usage_limit_per_user: 0,
        limit_usage_to_x_items: 0,
        free_shipping: false,
        product_categories: [],
        excluded_product_categories: [],
        exclude_sale_items: false,
        minimum_amount: "0",
        maximum_amount: "0",
        email_restrictions: [],
        used_by: [],
        meta_data: [],
        _links: {
          self: [{ href: "" }],
          collection: [{ href: "" }],
        },
      };
    }
  };

  async function checkCode() {
    if (!couponCode) {
      toast({
        title: "Please enter a coupon code.",
      });
      return;
    }
    setLoading(true);
    const coupon = await fetchCouponDetails(couponCode); // Wait for the API response

    if (coupon.code === "") {
      toast({
        title: "Invalid coupon code. Please try again.",
      });
      setLoading(false);
      return;
    }

    if (Number(coupon.minimum_amount) > total) {
      toast({
        title: `Can't apply coupon, minimum amount for discount is $${coupon.minimum_amount}`,
      });
      setLoading(false);
      return;
    }

    setDiscount(coupon.amount); // Ensure it's a number
    setType(coupon.discount_type || ""); // Default to empty string if undefined
    returnDiscountCode(coupon.code);

    // Store in localStorage
    localStorage.setItem("couponCode", couponCode);
    localStorage.setItem("discount", coupon.amount || "0");
    localStorage.setItem(
      "discountType",
      coupon.discount_type || ""
    );

    // Calculate new total
    let newTotal =
      total - (total * Number(coupon.amount)) / 100;
    setNewTotal(newTotal);
    localStorage.setItem(
      "newTotal",
      String(newTotal + shippingRate)
    );
    setLoading(false);
  }

  return (
    <div>
      <div className="flex justify-between items-center gap-5">
        <div className="grow flex gap-2 justify-end items-center">
          <Input
            placeholder="COUPON CODE"
            className="focus:outline-none sm:w-44 w-full rounded-none"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            type="search"
          />

          <Button
            onClick={() => checkCode()}
            className="rounded-none sm:w-36 w-24"
            disabled={!couponCode}
          >
            {loading ? (
              <Loader2 className="animate-spin duration-700" />
            ) : (
              <span>APPLY</span>
            )}
          </Button>
        </div>
      </div>
      {discount && couponCode !== "" && (
        <p className="mt-5 flex justify-between items-center">
          <span className="font-bold">
            {"Discount " + discount.slice(0, 2)}
            {type === "percent" ? "%" : "$"}
          </span>

          <span className="text-neutral-500 border border-neutral-500 pl-1 pr-1">
            {couponCode}
          </span>
        </p>
      )}
      <div className=" pt-5 pb-2 flex justify-between items-center">
        <p className="font-bold">TOTAL</p>
        <p className="font-bold">
          {(
            Number(newTotal) + Number(shippingRate)
          ).toFixed(2) ||
            (Number(total) + Number(shippingRate)).toFixed(
              2
            )}
          $
        </p>
      </div>
    </div>
  );
}
